import loginController from '@/scripts/libs/login/controller';
import { UserInfoCompat } from '@/scripts/libs/login/userResponseProcessor';
import viddsee from '@/scripts/viddsee';
import { ActionOption, ActionPermissionOptions } from './action.types';
import analytic from './analytic';

class PermissionError extends Error {
  constructor(message = 'This action cannot be performed') {
    super(message);
  }
};
class NeedLoginPermissionError extends PermissionError {
  constructor(message = 'User is not login') {
    super(message);
  }
};

const makeDefaultPermissions = (permissions: ActionPermissionOptions = {}): Required<ActionPermissionOptions> => ({
  show: true,
  actionable: true,
  needLogin: false,
  ...permissions,
});

const actionHandlerGenerator = (
  instance: Action,
  action: ActionOption['action'],
  props: unknown,
) => (clickEvent: Event): void => {
  if (!instance.permissions.actionable || !instance.permissions.show) {
    if (clickEvent && typeof clickEvent.preventDefault === 'function') {
      clickEvent.preventDefault();
    }
    return;
  }

  const loginPromiseResolver: Promise<UserInfoCompat | void> = instance.permissions.needLogin
    ? loginController.getUser()
      .then(user => {
        if (!user) throw new NeedLoginPermissionError();
        return user;
      })
    : Promise.resolve();

  loginPromiseResolver
    .then(() => {
      if (typeof action !== 'function') return;

      // If everything is fine, execute action with this supplied
      action(props, instance, clickEvent);
      if (instance.tracker) {
        const tracker = (typeof instance.tracker === 'function') ? instance.tracker(props) : instance.tracker;
        analytic.send(tracker);
      }
    })
    .catch((error) => {
      if (error instanceof NeedLoginPermissionError) {
        viddsee.ui.modal.register.open({
          loginReason: instance.permissionRejectionMessages.needLogin,
        });
      } else {
        throw error;
      }
    });
};

class Action {
  permissions!: Required<ActionPermissionOptions>;

  permissionRejectionMessages!: NonNullable<ActionOption['permissionRejectionMessages']>;

  show!: boolean;

  label!: ActionOption['label'];

  icon: ActionOption['icon'];

  url: ActionOption['url'];

  action!: (clickEvent: Event) => void;

  tracker!: NonNullable<ActionOption['tracker']> | null

  constructor(options: ActionOption, props: any) {
    this.updateAction(options, props);
  }

  /**
   * Prop options can be can used to configure action render during runtime
   */
  updateAction(options: ActionOption, props: unknown): void {
    // Setup actions
    this.permissions = makeDefaultPermissions(options.permissions);
    this.permissionRejectionMessages = { ...options.permissionRejectionMessages };

    // Visibility control
    this.show = this.permissions.show;

    this.label = options.label;
    this.icon = options.icon;
    this.url = options.url;

    // Actions - usually binded to click handler
    this.action = actionHandlerGenerator(this, options.action, props);

    // Tracking
    this.tracker = options.tracker || null;
  }
}

export default Action;
